import { memo, useMemo } from 'react';

import { type CustomFieldTypeName } from '@air/api/types';
import { Field as FieldIcon } from '@air/next-icons';
import { Button } from '@air/primitive-button';

import {
  RecentlyDeletedTableRow,
  type RecentlyDeletedTableRowProps,
} from '~/components/RecentlyDeleted/components/RecentlyDeletedTableRow';
import { customFieldIconMap } from '~/constants/customFieldIconMap';

export type RecentlyDeletedCustomFieldTableRowProps = Pick<
  RecentlyDeletedTableRowProps,
  'dateDeleted' | 'recoveryWindow' | 'deletedBy' | 'style' | 'title'
> & {
  onRecoverCustomField: () => void;
  fieldType: CustomFieldTypeName;
};

export const RecentlyDeletedCustomFieldTableRow = memo(
  ({
    dateDeleted,
    recoveryWindow,
    deletedBy,
    onRecoverCustomField,
    style,
    title,
    fieldType,
  }: RecentlyDeletedCustomFieldTableRowProps) => {
    const thumbnailEl = useMemo(() => {
      const Icon = customFieldIconMap[fieldType] ?? FieldIcon;

      return (
        <div className="flex size-8 items-center justify-center rounded-4 border border-grey-5 bg-grey-2">
          <Icon className="size-4 text-grey-11" />
        </div>
      );
    }, [fieldType]);

    const actionsEl = useMemo(
      () => (
        <Button appearance="ghost" className="-ml-2.5" onClick={onRecoverCustomField}>
          Recover
        </Button>
      ),
      [onRecoverCustomField],
    );

    return (
      <RecentlyDeletedTableRow
        actions={actionsEl}
        deletedBy={deletedBy}
        dateDeleted={dateDeleted}
        recoveryWindow={recoveryWindow}
        style={style}
        thumbnail={thumbnailEl}
        title={title}
        type="customField"
      />
    );
  },
);

RecentlyDeletedCustomFieldTableRow.displayName = 'RecentlyDeletedCustomFieldTableRow';
